/**
 * Supplier portal: header for the newest Files → RFQs request round on a project.
 * A round is one RFQ — label, Concept / Production, sent/due dates, staff message,
 * and the structures it covers — answered with one quote upload.
 */
import React from 'react';
import { CalendarClock, ClipboardList } from 'lucide-react';
import { rfqRequestStageLabel } from '../../utils/rfqRequestStage';
import type { VendorRequestRound } from '../../utils/vendorPortalDealGroups';

const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const VendorPortalRequestHeader: React.FC<{ round: VendorRequestRound }> = ({ round }) => {
  const stageLabel = rfqRequestStageLabel(round.stage) ?? null;
  const overdue = !!round.quoteDeadline && round.open && round.quoteDeadline.getTime() < Date.now();
  const structures = round.structureNames;
  const singleUpload = round.packages.length === 1;
  return (
    <section className={`vp-request-head${round.open ? ' vp-request-head--open' : ''}`} aria-label="Quote request">
      <div className="vp-request-head-row">
        <ClipboardList size={15} aria-hidden className="vp-request-head-icon" />
        <h3 className="vp-request-head-title">{round.label}</h3>
        {stageLabel ? (
          <span className={`vp-request-chip vp-request-chip--stage-${stageLabel.toLowerCase()}`} title={
            stageLabel === 'Production'
              ? 'Firm pricing on the list we will cut to.'
              : 'Budgetary pricing on a concept-level list — the frame may still change.'
          }>
            {stageLabel} pricing
          </span>
        ) : null}
        <span className={`vp-request-chip ${round.open ? 'vp-request-chip--open' : 'vp-request-chip--done'}`}>
          {round.open ? 'Awaiting your quote' : 'Answered'}
        </span>
        {round.quoteDeadline ? (
          <span className={`vp-request-chip vp-request-chip--due${overdue ? ' vp-request-chip--overdue' : ''}`} title={round.quoteDeadline.toLocaleString()}>
            <CalendarClock size={11} aria-hidden /> Due {fmt(round.quoteDeadline)}
            {overdue ? ' · overdue' : ''}
          </span>
        ) : null}
        <span className="vp-request-head-meta">
          {round.sentAt ? `Sent ${fmt(round.sentAt)} · ` : ''}
          {structures.length
            ? `${structures.length} structure${structures.length !== 1 ? 's' : ''}: ${structures.join(', ')}`
            : 'Whole project'}
        </span>
      </div>
      {round.message ? <p className="vp-request-head-message">“{round.message}”</p> : null}
      <p className="vp-request-head-hint">
        {singleUpload
          ? structures.length > 1
            ? 'One combined timber list covers every structure above. Upload a quote for each structure below — or one quote covering several if you price them together.'
            : 'Upload one quote for this request below.'
          : 'This request was sent as separate lists — upload one quote per list below.'}
      </p>
    </section>
  );
};

export default VendorPortalRequestHeader;
